/**
 * Choosing how deep to look at a site.
 *
 * Two choices, behind a custom dropdown with its own open state, its own
 * outside-click listener, and its own `role="listbox"` built out of buttons.
 * Quick was amber with a lightning bolt, Smart was accent with a magnifier,
 * the locked badge was chartreuse uppercase monospace, the scan-limit notice
 * amber and the failure notice red. Five colours and a bespoke widget, for a
 * question with two answers.
 *
 * Two answers is a pair of radios. The whole dropdown, both effects and the
 * ref are gone with it.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { apiService } from '@/services/api'
import DomainLimitModal from '@/components/DomainLimitModal'
import { getDefaultQuickScan } from '@/utils/siteScanMode'
import { buildDomainReportScanUrl } from '@/utils/siteScanNavigation'
import { Button, ButtonLink, Dialog, Notice, Radio } from '@/design-system/primitives'
import { NeedsPlan } from '@/design-system/marks'
import '@/design-system/dashboard.css'
import '@/design-system/form.css'

const BILLING_PLANS_PATH = '/settings/billing?section=plans#subscription-plans'

interface ScanTypeModalProps {
  isOpen: boolean
  onClose: () => void
  domain: string
  displayName: string
  isPro: boolean
  isRescan: boolean
}

export default function ScanTypeModal({
  isOpen,
  onClose,
  domain,
  displayName,
  isPro,
  isRescan,
}: ScanTypeModalProps) {
  const navigate = useNavigate()
  const [isQuickScan, setIsQuickScan] = useState(() =>
    getDefaultQuickScan({ isSubscriber: isPro, isRescan })
  )
  const [isStarting, setIsStarting] = useState(false)

  const [siteLimitError, setSiteLimitError] = useState<{ message: string; limit: number; current: number } | null>(null)
  const [rateLimitError, setRateLimitError] = useState<{ limit: number; used: number } | null>(null)
  const [generalError, setGeneralError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setIsQuickScan(getDefaultQuickScan({ isSubscriber: isPro, isRescan }))
    setIsStarting(false)
    setSiteLimitError(null)
    setRateLimitError(null)
    setGeneralError(null)
  }, [isOpen, isPro, isRescan])

  const handleSubmit = async () => {
    setSiteLimitError(null)
    setRateLimitError(null)
    setGeneralError(null)
    setIsStarting(true)

    try {
      const response = await apiService.startAIVisibilityScan(domain, {
        forceRescan: isRescan,
        quickScan: isQuickScan,
      })

      if (response.success && response.scan) {
        onClose()
        toast.success(`Reading ${displayName}`)
        navigate(buildDomainReportScanUrl(domain, response.scan.id))
        return
      }
      setGeneralError('We could not start the scan.')
    } catch (error: any) {
      if (error.response?.status === 403 && error.response?.data?.error === 'Site limit reached') {
        setSiteLimitError({
          message: error.response.data.message,
          limit: error.response.data.limit || 1,
          current: error.response.data.current || 1,
        })
      } else if (error.response?.status === 429) {
        setRateLimitError({
          limit: error.response.data.limit || 3,
          used: error.response.data.used || 3,
        })
      } else {
        setGeneralError(
          error.response?.data?.message || error.message || 'We could not start the scan.'
        )
      }
    } finally {
      setIsStarting(false)
    }
  }

  /* A request is in flight and the dialog owns it, so it does not go away
     under the person who started it. */
  const handleClose = () => {
    if (isStarting) return
    onClose()
  }

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        title={isRescan ? `Look at ${displayName} again` : `Look at ${displayName}`}
        lead={domain}
        actions={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={isStarting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={isStarting}>
              {isRescan ? 'Read it again' : 'Read the site'}
            </Button>
          </>
        }
      >
        <div className="ds-dash__scanpick">
          <Radio
            name="scan-depth"
            checked={isQuickScan}
            onChange={() => setIsQuickScan(true)}
            label={
              <span className="ds-dash__scanopt">
                <b>The important pages</b>
                <span>Ten pages, about thirty seconds.</span>
              </span>
            }
          />
          <Radio
            name="scan-depth"
            checked={!isQuickScan}
            disabled={!isPro}
            onChange={() => isPro && setIsQuickScan(false)}
            label={
              <span className="ds-dash__scanopt">
                <b>
                  The whole site
                  {!isPro && <NeedsPlan plan="Starter" />}
                </b>
                <span>
                  {isPro
                    ? 'Up to a hundred pages across the site.'
                    : 'Reads across the whole site rather than the front of it, which is where most of what is missing turns out to be.'}
                </span>
              </span>
            }
          />
          {!isPro && (
            <ButtonLink to={BILLING_PLANS_PATH} variant="secondary" size="sm">
              See the plans
            </ButtonLink>
          )}
        </div>

        {rateLimitError && (
          <div className="ds-dash__answer">
            <Notice kind="look">
              <b>You have used this month's scans</b>
              <p>
                {rateLimitError.used} of {rateLimitError.limit} used. It resets with your billing
                month.
              </p>
            </Notice>
          </div>
        )}

        {generalError && (
          <div className="ds-dash__answer">
            <Notice kind="look">
              <b>We could not start it</b>
              <p>{generalError}</p>
            </Notice>
          </div>
        )}
      </Dialog>

      <DomainLimitModal
        isOpen={!!siteLimitError}
        onClose={() => setSiteLimitError(null)}
        currentCount={siteLimitError?.current ?? 1}
        maxCount={siteLimitError?.limit ?? 1}
        reason="scan another site"
        triggerSource="ScanTypeModal"
      />
    </>
  )
}
